import {
  type ReactNode,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  type ComboBoxOptionType,
  DiscreteInput,
  type DiscreteOption,
} from "@/components/RightStyle/TailwindStylingPanel/components/input";
import { useTailwindProperties } from "@/hooks/useTailwindProperties";
import { safeParseCssStringAsInlineStyles } from "../../utils";
import useGradient, { type GradientState } from "../../hooks/useGradient";
import type { TempoElement } from "@/utils/tempoElement";
import {
  ArrowBottomLeftIcon,
  ArrowBottomRightIcon,
  ArrowTopLeftIcon,
  ArrowTopRightIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  OpacityIcon,
} from "@radix-ui/react-icons";
import React from "react";
import CustomColorPicker from "../input/CustomColorPicker";
import CustomImageUpload from "../input/CustomImageUpload";
import GradientColorPicker from "../input/GradientColorPicker";
import GradientControls from "../input/GradientControls";
import HexInput from "../input/HexInput";
import LibraryColorPanel from "../LibraryColorPanel";
import {
  GRADIENT_PROPERTY_TYPES,
  PropertyType,
  type ClassTypeWithPropertyType,
} from "@/tailwind/tailwindUtils";
import { useEditingClassesByCodebaseId } from "@/store/zustand/designCommandManagerStore";
import type { JsxElement } from "@/utils/jsxElementTypes";
import { useNewPropertyState2 } from "@/components/RightStyle/usePropertyStateV2";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { noop } from "lodash";
import { VariantControl } from "../variant/VariantControl";
import { VariantDraggableInput } from "../input/VariantDraggableInput";
import {
  parseColorValue,
  normalizeColorValue,
} from "../input/color-picker/utils/colorFormatUtils";
import {
  Tabs as TabsUnstyled,
  TabsList as TabsListUnstyled,
  Tab as TabUnstyled,
  TabPanel as TabPanelUnstyled,
} from "@mui/base";
import type { CssFieldValue } from "@/components/RightStyle";
import { cn } from "@/lib/utils";

// Add type for color option
interface ColorOption extends ComboBoxOptionType {
  value?: string;
}

interface Props {
  selectedElement?: TempoElement | null;
  selectedTreeElement?: JsxElement | null;
  pickTailwindClassForProperty: (args: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
    elementClasses?: string[];
  }) => void;
  updateInstantForProperty: (args: {
    propertyType: PropertyType;
    option: ComboBoxOptionType;
    setTemporaryDraggingValue?: (value: string | null) => void;
  }) => void;
  removeClassForProperty: (args: { propertyType: PropertyType }) => void;
  removeClassesForMultipleProperties: (args: {
    propertyTypes: PropertyType[];
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
    thenSetOption?: ComboBoxOptionType;
    thenSetPropertyType?: PropertyType;
  }) => void;
  cssValues: { [cssName: string]: CssFieldValue };
  onOpenFillVariantModal?: () => void;
  fillVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  modalPopoverRef?: React.RefObject<HTMLDivElement>;
  onOpenFromVariantModal?: () => void;
  fromVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onOpenViaVariantModal?: () => void;
  viaVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onOpenToVariantModal?: () => void;
  toVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
}

enum MainTabType {
  Custom = "custom",
  Library = "library",
}

enum ViewType {
  Fill = "fill",
  Gradient = "gradient",
  Image = "image",
}

const cleanColorValue = (value: string): string => {
  // Handle Tailwind class format
  if (value.startsWith("bg-[")) {
    return value.replace(/^bg-\[(.+)\]$/, "$1");
  }
  // Handle CSS property format
  if (value.includes("background-color:")) {
    return value.replace(/^background-color:\s*/, "").trim();
  }
  // Handle raw color value
  return value.trim();
};

const viewOptions: DiscreteOption[] = [
  {
    key: ViewType.Fill,
    option: {
      tailwind: "",
      css: "",
    },
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">Solid</div>
    ),
  },
  {
    key: ViewType.Gradient,
    option: {
      tailwind: "",
      css: "",
    },
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">
        Gradient
      </div>
    ),
  },
  {
    key: ViewType.Image,
    option: {
      tailwind: "",
      css: "",
    },
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">Image</div>
    ),
  },
];

const FillModal: React.FC<Props> = ({
  selectedTreeElement,
  pickTailwindClassForProperty,
  updateInstantForProperty,
  removeClassForProperty,
  removeClassesForMultipleProperties,
  cssValues,
  selectedElement,
  onOpenFillVariantModal,
  fillVariantAnchorRef,
  modalPopoverRef,
  onOpenFromVariantModal,
  fromVariantAnchorRef,
  onOpenViaVariantModal,
  viaVariantAnchorRef,
  onOpenToVariantModal,
  toVariantAnchorRef,
}) => {
  const isDraggingRef = useRef(false);

  const { classes: elementClasses } = useEditingClassesByCodebaseId(
    selectedTreeElement || null
  );

  const tailwindClassesMap = useCodebaseStore(
    (state) => state.tailwindClassesMap
  );

  const hasGradientClasses = useCallback(() => {
    return elementClasses.some(
      (cls) =>
        cls.startsWith("bg-gradient-") ||
        cls.startsWith("from-") ||
        cls.startsWith("to-") ||
        cls.startsWith("via-")
    );
  }, [elementClasses]);

  const hasImageClasses = useCallback(() => {
    return elementClasses.some((cls) => cls.startsWith("bg-[url("));
  }, [elementClasses]);

  const getCurrentFillType = useCallback(() => {
    const backgroundImageValue = cssValues["background-image"];
    const hasGradients = hasGradientClasses();
    const hasImages = hasImageClasses();

    // Check for gradients first (classes or computed style)
    if (
      hasGradients ||
      backgroundImageValue?.evaluatedValue?.includes("gradient") ||
      backgroundImageValue?.value?.includes("gradient")
    ) {
      return ViewType.Gradient;
    }

    // Check for images
    if (
      hasImages ||
      backgroundImageValue?.evaluatedValue?.includes("url") ||
      backgroundImageValue?.value?.includes("url")
    ) {
      return ViewType.Image;
    }

    // Default to solid fill
    return ViewType.Fill;
  }, [cssValues, hasGradientClasses, hasImageClasses]);

  const backgroundColorProperties = useTailwindProperties(
    PropertyType.BACKGROUND_COLOR
  );

  const isColorClass = useCallback((cls: string) => {
    return (
      cls.startsWith("bg-[") ||
      (cls.startsWith("bg-") && !cls.startsWith("bg-gradient-"))
    );
  }, []);

  const isDirectionValue = useCallback((val: string) => {
    return val.startsWith("to ") || val.startsWith("bg-gradient-to-");
  }, []);

  const gradientClasses = elementClasses.filter((cls) =>
    cls.startsWith("bg-gradient-")
  );
  const colorClasses = elementClasses.filter(isColorClass);

  const backgroundColorState = useNewPropertyState2({
    elementClasses: colorClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BACKGROUND_COLOR,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });

  // Helper function to detect if current background color is a CSS custom property
  const isCustomColor = useCallback(() => {
    const currentClass = elementClasses.find(isColorClass);
    if (!currentClass) return false;

    // Check if it's an arbitrary class with CSS custom property
    return currentClass.includes("bg-[") && currentClass.includes("var(--");
  }, [elementClasses, isColorClass]);

  // Set initial tab based on whether current color is a custom color, but allow user to change it freely
  const [mainTab, setMainTab] = useState<MainTabType>(() => {
    return isCustomColor() ? MainTabType.Library : MainTabType.Custom;
  });
  const [view, setView] = useState<ViewType>(getCurrentFillType);

  const backgroundImageState = useNewPropertyState2({
    elementClasses: gradientClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BACKGROUND_IMAGE,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });

  const fromColorProperties = useTailwindProperties(PropertyType.FROM_COLOR);
  const toColorProperties = useTailwindProperties(PropertyType.TO_COLOR);
  const viaColorProperties = useTailwindProperties(PropertyType.VIA_COLOR);
  const fromPositionProperties = useTailwindProperties(
    PropertyType.FROM_POSITION
  );
  const viaPositionProperties = useTailwindProperties(
    PropertyType.VIA_POSITION
  );
  const toPositionProperties = useTailwindProperties(PropertyType.TO_POSITION);

  const fromPositionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FROM_POSITION,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });
  const viaPositionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.VIA_POSITION,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });
  const toPositionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TO_POSITION,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });

  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
  const [imageRemovedThisSession, setImageRemovedThisSession] = useState(false);

  const hasGradientClass = elementClasses.some((cls) =>
    cls.startsWith("bg-gradient-")
  );
  const computedGradient = useGradient(
    cssValues["background-image"]?.evaluatedValue,
    elementClasses,
    selectedElement,
    hasGradientClass
  );

  const extractSolidColor = useCallback(() => {
    const colorClass = elementClasses.find(isColorClass);
    if (colorClass) {
      return normalizeColorValue(colorClass);
    }
    return "";
  }, [elementClasses, isColorClass]);

  const extractGradientDirection = useCallback(() => {
    const directionClass = elementClasses.find(isDirectionValue);
    if (directionClass) {
      return directionClass;
    }
    return "to right";
  }, [elementClasses, isDirectionValue]);

  const [solidColorValue, setSolidColorValue] = useState<string>(
    extractSolidColor()
  );
  const [localGradient, setLocalGradient] = useState<GradientState | null>(
    hasGradientClass && computedGradient
      ? { ...computedGradient, direction: extractGradientDirection() }
      : { direction: "to right" }
  );

  const opacityProperties = useTailwindProperties(PropertyType.OPACITY);
  const opacityState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.OPACITY,
    cssValues: cssValues as {
      [cssName: string]: {
        value: string;
        cssName: string;
        evaluatedValue?: string;
      };
    },
  });

  useEffect(() => {
    // Skip updates during dragging since the color picker is handling them
    if (isDraggingRef.current) return;

    if (backgroundColorState.propertyValue) {
      const solidColor = extractSolidColor();
      if (solidColor) {
        setSolidColorValue(cleanColorValue(solidColor));
      }
    } else {
      const gradientDirection = extractGradientDirection();
      if (gradientDirection) {
        setLocalGradient((prev) => ({
          ...prev,
          direction: gradientDirection,
        }));
      }
    }
  }, [
    backgroundColorState.propertyValue,
    extractSolidColor,
    extractGradientDirection,
  ]);

  const removeImageCompletely = () => {
    // Synchronously clear local image preview
    if (localImageUrl) {
      URL.revokeObjectURL(localImageUrl);
      setLocalImageUrl(null);
    }
    removeClassForProperty({ propertyType: PropertyType.BACKGROUND_IMAGE });
  };

  const removeImageIfPresent = () => {
    if (backgroundImageState.propertyValue?.includes("url(")) {
      removeImageCompletely();
    }
  };

  const handleSetSolidColor = (option: ComboBoxOptionType) => {
    // Clean up gradients and images when setting solid color using the proper property type approach
    const hasGradients = hasGradientClasses();
    const hasImages = hasImageClasses();

    if (hasGradients || hasImages) {
      // Define all gradient-related property types to remove

      // Clear all gradient-related property types and set the new solid color atomically
      removeClassesForMultipleProperties({
        propertyTypes: GRADIENT_PROPERTY_TYPES,
        setTemporaryDraggingValue:
          backgroundColorState.setTemporaryDraggingValue,
        thenSetOption: option,
        thenSetPropertyType: PropertyType.BACKGROUND_COLOR,
      });

      setSolidColorValue(cleanColorValue(option.tailwind));
      return;
    }

    setSolidColorValue(cleanColorValue(option.tailwind));
    pickTailwindClassForProperty({
      option,
      propertyType: PropertyType.BACKGROUND_COLOR,
      setTemporaryDraggingValue: backgroundColorState.setTemporaryDraggingValue,
    });
  };

  const handleSetGradient = (
    option: ComboBoxOptionType,
    propertyType: PropertyType
  ) => {
    // Clean up solid colors and images when setting gradient
    const hasSolidColors = elementClasses.some(
      (cls) =>
        cls.startsWith("bg-") &&
        !cls.startsWith("bg-gradient-") &&
        !cls.startsWith("bg-[url(")
    );
    const hasImages = elementClasses.some((cls) => cls.startsWith("bg-[url("));

    if (hasSolidColors) {
      removeClassForProperty({ propertyType: PropertyType.BACKGROUND_COLOR });
    }
    if (hasImages) {
      removeImageCompletely();
    }

    pickTailwindClassForProperty({
      option,
      propertyType,
      setTemporaryDraggingValue: backgroundImageState.setTemporaryDraggingValue,
    });
    setLocalGradient(
      computedGradient
        ? { ...computedGradient, direction: extractGradientDirection() }
        : { direction: "to right" }
    );
  };

  const buildBackgroundColorPreview = (
    option: ComboBoxOptionType
  ): ReactNode => {
    const styles = safeParseCssStringAsInlineStyles(option.css);
    return <div className={"w-full h-full"} style={styles} />;
  };

  const backgroundImageProperties = useTailwindProperties(
    PropertyType.BACKGROUND_IMAGE
  );

  const [gradientDirectionIcon, setGradientDirectionIcon] = useState(
    <ArrowRightIcon />
  );

  useEffect(() => {
    if (computedGradient) {
      setLocalGradient(computedGradient);

      switch (computedGradient.direction) {
        case "to top":
          setGradientDirectionIcon(<ArrowUpIcon />);
          break;
        case "to right":
          setGradientDirectionIcon(<ArrowRightIcon />);
          break;
        case "to bottom":
          setGradientDirectionIcon(<ArrowDownIcon />);
          break;
        case "to left":
          setGradientDirectionIcon(<ArrowLeftIcon />);
          break;
        case "to top right":
        case "to right top":
          setGradientDirectionIcon(<ArrowTopRightIcon />);
          break;
        case "to top left":
        case "to left top":
          setGradientDirectionIcon(<ArrowTopLeftIcon />);
          break;
        case "to bottom right":
        case "to right bottom":
          setGradientDirectionIcon(<ArrowBottomRightIcon />);
          break;
        case "to bottom left":
        case "to left bottom":
          setGradientDirectionIcon(<ArrowBottomLeftIcon />);
          break;
        default:
          setGradientDirectionIcon(<ArrowRightIcon />);
      }
    } else {
      setLocalGradient(null);
    }
  }, [computedGradient]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: don't want to re-run this effect when the backgroundImageState changes
  useEffect(() => {
    if (localImageUrl) {
      backgroundImageState.setTemporaryDraggingValue(`url('${localImageUrl}')`);
    }

    // Cleanup function to revoke previous blob URL to prevent memory leaks
    return () => {
      if (localImageUrl) {
        URL.revokeObjectURL(localImageUrl);
      }
    };
  }, [localImageUrl]);

  const handleColorPickerPointerDown = () => {
    if (
      !imageRemovedThisSession &&
      backgroundImageState.propertyValue &&
      backgroundImageState.propertyValue.includes("url(")
    ) {
      removeImageCompletely();
      setImageRemovedThisSession(true);
    }
  };

  useEffect(() => {
    if (view !== ViewType.Fill) {
      setImageRemovedThisSession(false);
    }
  }, [view]);

  return (
    <div
      className="grid grid-cols-14 items-center gap-x-3 gap-y-2"
      ref={modalPopoverRef}
    >
      {/* Main tabs */}

      <div className="col-span-14">
        <TabsUnstyled
          value={mainTab}
          onChange={(_, value) => setMainTab(value as MainTabType)}
          className="w-full"
        >
          <TabsListUnstyled className="tab-list w-full flex font-medium text-xs p-2 pb-3 gap-2.5">
            <TabUnstyled
              className={cn(
                "p-0 text-xs font-semibold transition-colors hover:text-text-primary",
                mainTab === MainTabType.Custom
                  ? "text-secondary-900"
                  : "text-secondary-700"
              )}
              value={MainTabType.Custom}
            >
              Custom
            </TabUnstyled>
            <TabUnstyled
              className={cn(
                "p-0 text-xs font-semibold transition-colors hover:text-text-primary",
                mainTab === MainTabType.Library
                  ? "text-secondary-900"
                  : "text-secondary-700"
              )}
              value={MainTabType.Library}
            >
              Library
            </TabUnstyled>
          </TabsListUnstyled>

          {/* Library tab content */}
          <TabPanelUnstyled value={MainTabType.Library}>
            <div className="col-span-14">
              <LibraryColorPanel
                onColorSelected={(option) => {
                  handleSetSolidColor(option);
                }}
                comboboxOptions={backgroundColorProperties}
              />
            </div>
          </TabPanelUnstyled>

          {/* Custom tab content */}
          <TabPanelUnstyled value={MainTabType.Custom}>
            <>
              {(view === ViewType.Fill || view === ViewType.Gradient) && (
                <div className="col-span-14" style={{ position: "relative" }}>
                  {view === ViewType.Fill ? (
                    <CustomColorPicker
                      propertyState={{
                        ...backgroundColorState,
                        propertyValue: solidColorValue,
                        displayValue: solidColorValue,
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `bg-[${input}]`,
                          css: `background-color: ${input}`,
                        };
                      }}
                      instantUpdateValue={(option) => {
                        isDraggingRef.current = true;
                        updateInstantForProperty({
                          propertyType: PropertyType.BACKGROUND_COLOR,
                          option,
                          setTemporaryDraggingValue:
                            backgroundColorState.setTemporaryDraggingValue,
                        });
                        setSolidColorValue(cleanColorValue(option.css));
                      }}
                      comboboxOptions={backgroundColorProperties}
                      setValue={(option) => {
                        isDraggingRef.current = false;
                        handleColorPickerPointerDown();
                        handleSetSolidColor(option);
                      }}
                      onDropdownSelected={(option) => {
                        let comboBoxOption = backgroundColorProperties.find(
                          (opt) =>
                            opt.tailwind === option.value ||
                            opt.tailwind === `bg-${option.value}`
                        );
                        if (!comboBoxOption) {
                          comboBoxOption = {
                            tailwind:
                              typeof option.value === "string"
                                ? `bg-${option.value}`
                                : "",
                            css: "",
                            propertyType: PropertyType.BACKGROUND_COLOR,
                            ...(option as unknown as { value: string }),
                          };
                        }
                        handleSetSolidColor(comboBoxOption);
                      }}
                      onBeforeSetValue={removeImageIfPresent}
                      buildOptionPreview={buildBackgroundColorPreview}
                      tooltipContent="Background color"
                      onClear={() => {
                        removeClassForProperty({
                          propertyType: PropertyType.BACKGROUND_COLOR,
                        });
                        setSolidColorValue("");
                      }}
                      showHex={false}
                      onOpenVariantModal={onOpenFillVariantModal}
                      onPointerDown={handleColorPickerPointerDown}
                    />
                  ) : (
                    <GradientColorPicker
                      classList={elementClasses}
                      gradient={localGradient || { direction: "to right" }}
                      setGradient={setLocalGradient}
                      instantUpdateValue={(option) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.FROM_COLOR,
                          option,
                          setTemporaryDraggingValue:
                            backgroundImageState.setTemporaryDraggingValue,
                        });
                      }}
                      pickTailwindClassForProperty={
                        pickTailwindClassForProperty
                      }
                    />
                  )}
                  <button
                    type="button"
                    ref={fillVariantAnchorRef}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      width: 1,
                      height: 1,
                      opacity: 0,
                      pointerEvents: "none",
                    }}
                    tabIndex={-1}
                    aria-hidden
                  />
                </div>
              )}

              {view === ViewType.Image && (
                <CustomImageUpload
                  value={
                    localImageUrl
                      ? `url('${localImageUrl}')`
                      : backgroundImageState.propertyValue
                  }
                  evaluatedValue={
                    localImageUrl
                      ? `url('${localImageUrl}')`
                      : backgroundImageState.propertyValue
                  }
                  setValue={(val) => {
                    backgroundImageState.setTemporaryDraggingValue(val);
                  }}
                  set={backgroundImageState.isSet}
                  setSet={noop}
                  buildOptionPreview={buildBackgroundColorPreview}
                  buildArbitraryClass={(input: string) => {
                    const url = `url('${input}')`;
                    return {
                      tailwind: `bg-[${url}]`,
                      css: `background-image: ${url}`,
                    };
                  }}
                  onComboboxSelected={(option) => {
                    // Clean up solid colors and gradients when setting image
                    const hasSolidColors = elementClasses.some(
                      (cls) =>
                        cls.startsWith("bg-") &&
                        !cls.startsWith("bg-gradient-") &&
                        !cls.startsWith("bg-[url(")
                    );
                    const hasGradients = hasGradientClasses();

                    if (hasSolidColors) {
                      removeClassForProperty({
                        propertyType: PropertyType.BACKGROUND_COLOR,
                      });
                    }
                    if (hasGradients) {
                      // Clear all gradient-related property types using the proper approach
                      removeClassesForMultipleProperties({
                        propertyTypes: GRADIENT_PROPERTY_TYPES,
                        setTemporaryDraggingValue:
                          backgroundImageState.setTemporaryDraggingValue,
                        thenSetOption: option,
                        thenSetPropertyType: PropertyType.BACKGROUND_IMAGE,
                      });
                      return;
                    }

                    pickTailwindClassForProperty({
                      option,
                      propertyType: PropertyType.BACKGROUND_IMAGE,
                      setTemporaryDraggingValue:
                        backgroundImageState.setTemporaryDraggingValue,
                    });
                  }}
                  onClear={() => {
                    if (localImageUrl) {
                      URL.revokeObjectURL(localImageUrl);
                      setLocalImageUrl(null);
                    }
                    removeClassForProperty({
                      propertyType: PropertyType.BACKGROUND_IMAGE,
                    });
                  }}
                  onLocalPreview={(url) => setLocalImageUrl(url)}
                />
              )}

              {/* Custom view type tabs - only show in Custom main tab */}
              <div className="col-span-14">
                <DiscreteInput
                  className="p-0.5"
                  options={viewOptions}
                  onOptionSelected={(option) => {
                    setView(option.key as ViewType);
                  }}
                  selected={view}
                  set={false}
                />
              </div>
              <div className="col-span-14 flex items-center gap-2 w-full space-y-2 vertical-center">
                {view === ViewType.Fill && (
                  <div className="flex flex-row vertical-center gap-2 mt-2">
                    <HexInput
                      value={(() => {
                        // First try to get from CSS evaluated value
                        const backgroundColorValue =
                          cssValues["background-color"];
                        if (backgroundColorValue?.evaluatedValue) {
                          const parsed = parseColorValue(
                            backgroundColorValue.evaluatedValue,
                            backgroundColorProperties
                          );
                          if (parsed.isValid) {
                            return parsed.hex;
                          }
                        }

                        // Try to parse from the current solid color value
                        if (solidColorValue) {
                          // If solidColorValue is already a hex color, use it
                          if (solidColorValue.startsWith("#")) {
                            return solidColorValue;
                          }
                          // Otherwise, try to parse it as a Tailwind class or other format
                          const parsed = parseColorValue(
                            solidColorValue,
                            backgroundColorProperties
                          );
                          if (parsed.isValid) {
                            return parsed.hex;
                          }
                        }

                        // Try to parse from background color state
                        if (backgroundColorState.propertyValue) {
                          const parsed = parseColorValue(
                            backgroundColorState.propertyValue,
                            backgroundColorProperties
                          );
                          if (parsed.isValid) {
                            return parsed.hex;
                          }
                        }

                        // Default fallback
                        return "#000000";
                      })()}
                      onChange={(value) => {
                        // Update the solid color value for real-time feedback
                        setSolidColorValue(value);
                      }}
                      onCommit={(value) => {
                        // Create the arbitrary class and apply it
                        const option = {
                          tailwind: `bg-[${value}]`,
                          css: `background-color: ${value}`,
                        };
                        handleSetSolidColor(option);
                      }}
                      comboboxOptions={backgroundColorProperties}
                      onComboboxSelected={(option) => {
                        handleSetSolidColor(option);
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `bg-[${input}]`,
                          css: `background-color: ${input}`,
                        };
                      }}
                      buildOptionPreview={buildBackgroundColorPreview}
                      onOpenVariantModal={onOpenFillVariantModal}
                      variantAnchorRef={fillVariantAnchorRef}
                      onBeforeSetValue={removeImageIfPresent}
                      onClear={() => {
                        removeClassForProperty({
                          propertyType: PropertyType.BACKGROUND_COLOR,
                        });
                        setSolidColorValue("");
                      }}
                      placeholder="#000000"
                    />
                    <VariantDraggableInput
                      propertyState={{
                        ...opacityState,
                        displayValue:
                          opacityState.displayValue !== undefined &&
                          opacityState.displayValue !== null
                            ? `${Number.parseFloat(
                                Number(opacityState.displayValue).toFixed(2)
                              ).toString()}%`
                            : opacityState.displayValue,
                      }}
                      labelContent={<OpacityIcon />}
                      comboboxOptions={opacityProperties}
                      onComboboxSelected={(option, variant) => {
                        pickTailwindClassForProperty({
                          option,
                          propertyType: PropertyType.OPACITY,
                          setTemporaryDraggingValue:
                            opacityState.setTemporaryDraggingValue,
                          variant,
                        });
                      }}
                      onSliderValueUpdate={(option) => {
                        updateInstantForProperty({
                          propertyType: PropertyType.OPACITY,
                          option,
                          setTemporaryDraggingValue:
                            opacityState.setTemporaryDraggingValue,
                        });
                      }}
                      buildArbitraryClass={(input: string) => {
                        return {
                          tailwind: `opacity-[${input}]`,
                          css: `opacity: ${input}`,
                        };
                      }}
                      min={0}
                      max={100}
                      step={0.01}
                      tooltipContent="Opacity"
                      onClear={() => {
                        removeClassForProperty({
                          propertyType: PropertyType.OPACITY,
                        });
                      }}
                    />
                  </div>
                )}
              </div>

              {view === ViewType.Gradient && (
                <GradientControls
                  backgroundImageState={{
                    ...backgroundImageState,
                    propertyValue: hasGradientClass
                      ? backgroundImageState.propertyValue
                      : "to right",
                    displayValue: hasGradientClass
                      ? backgroundImageState.displayValue
                      : "to right",
                  }}
                  backgroundImageProperties={backgroundImageProperties}
                  backgroundColorProperties={backgroundColorProperties}
                  gradientDirectionIcon={gradientDirectionIcon}
                  setValue={(option) => {
                    if (option.tailwind.startsWith("from-")) {
                      handleSetGradient(option, PropertyType.FROM_COLOR);
                    } else if (option.tailwind.startsWith("to-")) {
                      handleSetGradient(option, PropertyType.TO_COLOR);
                    } else if (option.tailwind.startsWith("via-")) {
                      handleSetGradient(option, PropertyType.VIA_COLOR);
                    } else {
                      handleSetGradient(option, PropertyType.BACKGROUND_IMAGE);
                    }
                  }}
                  removeTailwindClass={() => {
                    removeClassForProperty({
                      propertyType: PropertyType.BACKGROUND_IMAGE,
                    });
                  }}
                  classList={elementClasses}
                  instantUpdateValue={(option) => {
                    updateInstantForProperty({
                      propertyType: PropertyType.BACKGROUND_IMAGE,
                      option,
                      setTemporaryDraggingValue:
                        backgroundImageState.setTemporaryDraggingValue,
                    });
                  }}
                  fromColorProperties={fromColorProperties}
                  toColorProperties={toColorProperties}
                  viaColorProperties={viaColorProperties}
                  fromPositionProperties={fromPositionProperties}
                  toPositionProperties={toPositionProperties}
                  viaPositionProperties={viaPositionProperties}
                  set={false}
                  setSet={() => {}}
                  onClear={() => {
                    removeClassForProperty({
                      propertyType: PropertyType.BACKGROUND_IMAGE,
                    });
                  }}
                  evaluatedValue={backgroundImageState.propertyValue}
                  gradient={localGradient || { direction: "to right" }}
                  setGradient={setLocalGradient}
                  buildArbitraryClass={(currentColor, input) => {
                    const option = {
                      tailwind: "",
                      css: "",
                      propertyType: PropertyType.BACKGROUND_IMAGE,
                    };
                    switch (currentColor) {
                      case "from":
                        option.tailwind = `from-[${input}]`;
                        option.css = `--tw-gradient-from: ${input} var(--tw-gradient-from-position);`;
                        break;
                      case "via":
                        option.tailwind = `via-[${input}]`;
                        option.css = `--tw-gradient-via: ${input} var(--tw-gradient-via-position);`;
                        break;
                      case "to":
                        option.tailwind = `to-[${input}]`;
                        option.css = `--tw-gradient-to: ${input} var(--tw-gradient-to-position);`;
                        break;
                    }
                    return option;
                  }}
                  onOpenFromVariantModal={onOpenFromVariantModal}
                  fromVariantAnchorRef={fromVariantAnchorRef}
                  onOpenViaVariantModal={onOpenViaVariantModal}
                  viaVariantAnchorRef={viaVariantAnchorRef}
                  onOpenToVariantModal={onOpenToVariantModal}
                  toVariantAnchorRef={toVariantAnchorRef}
                  pickTailwindClassForProperty={pickTailwindClassForProperty}
                  fromPositionState={fromPositionState}
                  viaPositionState={viaPositionState}
                  toPositionState={toPositionState}
                />
              )}
            </>
          </TabPanelUnstyled>
        </TabsUnstyled>
      </div>
    </div>
  );
};

export default React.memo(FillModal);
